import { useEffect, useRef, useState } from 'react';
import './admin-announcements.styles.scss';
import TextEditorNoLib from '@/components/SHARED/text-editor-no-lib/text-editor-no-lib.component';
import { getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { ModalAlert } from '@/components/modal/modal.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { RandomString } from '@/utils/randomString';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { getOptimizedImageNoCrop } from '@/utils/getOptimizedBlob';

const defaultForm = {
  title: '',
  messageHtml: '',
  startDate: '',
  endDate: '',
};

// Parse a YYYY-MM-DD string as local midnight to avoid UTC timezone shifts
function dateStringToMs(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
}

// Parse a YYYY-MM-DD string as 11:59 PM local time
function endDateStringToMs(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 0, 0).getTime();
}

// Convert a ms timestamp back to YYYY-MM-DD using local date methods
function msToDateString(ms) {
  const d = new Date(ms);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const AdminAnnouncements = () => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [announcements, setAnnouncements] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const dataRef = useRef({});
  const announcementsRef = useRef();
  const announcementsListener = useRef(null);

  useEffect(() => {
    if (!userProfile) return;

    function fetchAnnouncements() {
      getQueryFromFirestore({
        'path': 'platform_announcements',
        'callback': (data) => {
          announcementsRef.current = {
            ...announcementsRef.current,
            ...{ [data.id]: data }
          }

          setAnnouncements(
            Object.values(announcementsRef.current)
              .sort((a, b) => b.startDate - a.startDate)
            ?? []
          );
        },
        'handleListener': (listener) => {
          // No need to remove listener since this is only read once on load, but just in case:
          announcementsListener.current = listener;
        }
      });
    }

    fetchAnnouncements();

    return () => {
      if (announcementsListener.current) announcementsListener.current();
    }
  }, [userProfile]);


  function handleChange(e) {
    const { name, value } = e.target;
    dataRef.current = { ...dataRef.current, [name]: value };
    setFormData(dataRef.current);
  }

  function handleMessageChange(html) {
    dataRef.current = { ...dataRef.current, messageHtml: html };
    setFormData(dataRef.current);
  }

  async function handleImageUpload(file) {
    setLoading(true);
    if (file.type.includes('gif')) {
      setAlertMessage('GIF images are not supported. Please upload a JPG or PNG image.');
      setLoading(false);
      return;
    }
    const optimizedFile = await getOptimizedImageNoCrop(file, 20, 300);
    console.log('optimizedFile', optimizedFile);
    if (!optimizedFile) {
      setAlertMessage('There was an error processing the image. Please try a different image or try again later.');
      setLoading(false);
      return;
    }
    const reader = new FileReader();
    const imageUrl = await new Promise((resolve, reject) => {
      reader.onloadend = async () => {
        const base64String = reader.result.split(',')[1];
        const uploadRes = await UseCloudFunction(
          'uploadFileToStorage',
          {
            'fileDataBase64': base64String,
            'fileName': file.name,
            'fileType': file.type,
            'folder': userProfile.appId,
            'userProfile': userProfile.appId
          }
        )
        console.log(uploadRes);
        setLoading(false);
        if (uploadRes.error) {
          setAlertMessage(`Error uploading file: ${uploadRes.error}`);
          reject(uploadRes.error);
          return;
        }
        resolve(uploadRes.url);
      }
      reader.readAsDataURL(optimizedFile);
    });
    return imageUrl;
  }

  function loadForEdit(ann) {
    const editData = {
      ...ann,
      startDate: msToDateString(ann.startDate),
      endDate: msToDateString(ann.endDate),
    };
    dataRef.current = editData;
    setFormData(editData);
    setEditorKey(prev => prev + 1);
  }

  async function handleSave(e) {
    e.preventDefault();
    // Validation
    if (!formData.title || !formData.startDate || !formData.endDate) {
      alert('Please fill in all required fields.');
      return;
    }
    const startMs = dateStringToMs(formData.startDate);
    const endMs = endDateStringToMs(formData.endDate);
    if (startMs > endMs) {
      alert('Start date cannot be after end date.');
      return;
    }
    // Save to Firestore
    try {
      setLoading(true);
      const newAnnouncement = {
        ...dataRef.current,
        startDate: startMs,
        endDate: endMs,
      };

      if (!newAnnouncement.id) {
        newAnnouncement.id = `${new Date().getTime()}_announcement`;
        newAnnouncement.createdBy = userProfile.appId;
        newAnnouncement.path = RandomString(20);
      }

      await UseCloudFunction(
        'saveDocToFirestore',
        {
          path: `platform_announcements/${newAnnouncement.id}`,
          docData: newAnnouncement,
          createTimestamp: true,
        }
      )
    } catch (error) {
      setAlertMessage('Failed to save announcement. (Error: ' + error.message + ')');
      setLoading(false);
      return;
    }
    setLoading(false);
    dataRef.current = { ...defaultForm };
    setFormData({ ...defaultForm });
    setEditorKey(prev => prev + 1);
  }

  return (
    <div className='admin-announcements-container'>
      <div className='g-card'>
        <form onSubmit={handleSave}>
          <div className='section-title'>Create Announcement</div>
          <hr />
          <div className='field'>
            <label htmlFor='ann-title'>Title</label>
            <input
              id='ann-title'
              type='text'
              name='title'
              value={formData.title || ''}
              onChange={handleChange}
              placeholder='Announcement title'
              required
            />
          </div>
          <div className='g-space-1'></div>
          <div className='field'>
            <label>Message</label>
            {
              (formData) &&
              <TextEditorNoLib
                key={editorKey}
                onChange={handleMessageChange}
                initialValue={formData.messageHtml || ''}
                handleImageUpload={handleImageUpload}
              />
            }
          </div>
          <div className='g-space-1'></div>
          <div className='date-row'>
            <div className='field'>
              <label htmlFor='ann-start'>Start Date</label>
              <input
                id='ann-start'
                type='date'
                name='startDate'
                value={formData.startDate || ''}
                onChange={handleChange}
                required
              />
            </div>
            <div className='field'>
              <label htmlFor='ann-end'>End Date</label>
              <input
                id='ann-end'
                type='date'
                name='endDate'
                value={formData.endDate || ''}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className='g-space-1'></div>
          <div className='buttons'>
            <button type='submit' className='g-button'>
              Save Announcement
            </button>
          </div>
        </form>
        <div className="g-space-2"></div>
        <hr />
        <div className='section-title'>Existing Announcements</div>
        <div className="g-space-1"></div>
        {announcements === null ? (
          <p className='meta'>Loading...</p>
        ) : announcements.length === 0 ? (
          <p className='meta'>No announcements yet.</p>
        ) : (
          <div className='announcements-list'>
            {announcements.map(ann => (
              <div
                key={ann.id}
                className={`announcement-row ${ann.startDate > Date.now()
                  ? 'upcoming'
                  : (
                    ann.endDate < Date.now()
                      ? 'expired'
                      : 'active'
                  )
                  }`}>
                <div className='announcement-row-header'>
                  <span className='announcement-title'>{ann.title}</span>
                  <span className='meta'>
                    {ann.startDate && format(new Date(ann.startDate), 'MMM d, yyyy')}
                    {' – '}
                    {ann.endDate && format(new Date(ann.endDate), 'MMM d, yyyy')}
                  </span>
                  <button
                    type='button'
                    className='g-button small-btn'
                    onClick={() => loadForEdit(ann)}>
                    <FontAwesomeIcon icon={faPen} />
                    Edit
                  </button>
                </div>
                {ann.messageHtml && (
                  <div
                    className='announcement-message'
                    dangerouslySetInnerHTML={{ __html: ann.messageHtml }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <ModalAlert
        show={!!alertMessage}
        message={alertMessage}
        cancel={() => setAlertMessage(null)} />
      <FullPageLoader show={loading} />
    </div>
  );
};

export default AdminAnnouncements;
